import React, { useContext } from "react";
import moment from "moment";
import { batchGenerateFuelCode } from "apis";
import { getDrivers } from "apis/driver.api";
import { getVehicles } from "apis/vehicle.api";
import { ActionButton, Modal, Text } from "components/commons";
import ImportModal from "components/modals/import-modal/import-modal";
import { FleetContext } from "contexts";
import { DateTime, FuelCodeType, Products } from "enums";
import { useApi, useModal } from "hooks";
import locale from "localization";
import { formatDate } from "utils";
import { exportCSV, parseCSVFile } from "utils/object.utils";
import { isMobileNumberValid11, isNumeric } from "utils/text.utils";
import styles from "./import-fuel-code.module.scss";

const ImportFuelCodeModal = ({ ...importModal }) => {
  const resultModal = useModal();
  const { fleet } = useContext(FleetContext);
  const { fleetId } = fleet || {};

  const getDriversRequest = useApi({ api: getDrivers });
  const getVehiclesRequest = useApi({ api: getVehicles });
  const generateRequest = useApi({ api: batchGenerateFuelCode });

  const importFuelCodes = async ({ file }) => {
    const isAny = (text = "") => text.trim().toLowerCase() === "any";
    const isFlag = (text = "") => ["0", "1"].includes(text.trim());
    const isYes = (text = "") => text.trim() === "1";
    const productCodes = [Products.Diesel, Products.Gas91, Products.Gas95, Products.Gas97];
    const errorLabels = {
      productCode: locale.invalidProductCodeRow,
      literVolume: locale.invalidVolumeLimitRow,
      startDate: locale.invalidDateRow,
      durationDays: locale.invalidDurationPeriodRow,
      driver: locale.unregisteredDriverRow,
      vehicle: locale.unregisteredVehicleRow,
      autoRenew: locale.invalidAutoRenewRow,
      oneTimeUse: locale.invalidOneTimeUseRow,
      oneTimeUseLimit: locale.limitTooLargeForOneTimeUseRow,
      sendAsSms: locale.invalidSendAsSmsRow,
      noContactNumber: locale.noContactNumberToSendSmsRow,
      mobileNumber: locale.invalidDriverContactNumberRow,
    };

    const [{ drivers }, { vehicles }] = await Promise.all([
      getDriversRequest.request({ fleetId, status: "active", page: 1, perPage: 9999 }),
      getVehiclesRequest.request({ fleetId, status: "active", page: 1, perPage: 9999 }),
    ]);
    const driversByLicenseId = new Map(
      drivers.map((driver) => [driver.driverLicenseId.trim().toUpperCase(), driver]),
    );
    const vehiclesByPlateNumber = new Map(
      vehicles.map((vehicle) => [vehicle.plateNumber.trim().toUpperCase(), vehicle]),
    );

    const parsedFile = await parseCSVFile(file, [
      {
        text: "Product Code",
        key: "productCode",
        validation: (text) => ({
          error: !productCodes.includes(text.trim().toLowerCase()),
          errorType: "productCode",
        }),
      },
      {
        text: "Volume Redeem Limit",
        key: "literVolume",
        validation: (text) => {
          const volume = Number(text.trim());
          return {
            error: !text.trim() || !isNumeric(text.trim()) || volume < 1 || volume > 10000,
            errorType: "literVolume",
          };
        },
      },
      {
        text: "Eligible Period Start Date",
        key: "startDate",
        validation: (text) => {
          const startDate = moment(text.trim(), "YYYY-MM-DD", true);
          return {
            error: !startDate.isValid() || startDate.isBefore(moment().startOf("day")),
            errorType: "startDate",
          };
        },
      },
      {
        text: "Eligible Period Duration in Days",
        key: "durationDays",
        validation: (text) => {
          const duration = Number(text.trim());
          return {
            error: !text.trim() || !isNumeric(text.trim()) || duration < 1 || duration > 1000,
            errorType: "durationDays",
          };
        },
      },
      {
        text: "Driver License ID",
        key: "driverLicenseId",
        validation: (text) => ({
          error: !isAny(text) && !driversByLicenseId.has(text.trim().toUpperCase()),
          errorType: "driver",
        }),
      },
      {
        text: "Plate Number",
        key: "plateNumber",
        validation: (text) => ({
          error: !isAny(text) && !vehiclesByPlateNumber.has(text.trim().toUpperCase()),
          errorType: "vehicle",
        }),
      },
      {
        text: "Auto Renew",
        key: "autoRenew",
        validation: (text) => ({
          error: !isFlag(text),
          errorType: "autoRenew",
        }),
      },
      {
        text: "One-time Use",
        key: "oneTimeUse",
        validation: (text, row) => {
          if (!isFlag(text)) {
            return { error: true, errorType: "oneTimeUse" };
          }
          return {
            error: isYes(text) && Number(row.literVolume) > 200,
            errorType: "oneTimeUseLimit",
          };
        },
      },
      {
        text: "Send as SMS",
        key: "sendAsSms",
        validation: (text, row) => {
          if (!isFlag(text)) {
            return { error: true, errorType: "sendAsSms" };
          }
          return {
            error: isYes(text) && isAny(row.driverLicenseId) && !row.mobileNumber?.trim(),
            errorType: "noContactNumber",
          };
        },
      },
      {
        text: "Driver Contact Number",
        key: "mobileNumber",
        optional: true,
        validation: (text, row) => {
          const mobileNumber = text.trim();
          if (!isAny(row.driverLicenseId) || !isYes(row.sendAsSms) || !mobileNumber) {
            return { error: false };
          }
          const isTenDigitMobileNumber =
            mobileNumber.length === 10 && mobileNumber[0] === "9" && isNumeric(mobileNumber);
          return {
            error: !isMobileNumberValid11(mobileNumber) && !isTenDigitMobileNumber,
            errorType: "mobileNumber",
          };
        },
      },
    ]);

    const errors = [];
    if (parsedFile.invalidFormat || !parsedFile.items.length) {
      errors.push({ title: locale.otherError, items: [locale.invalidFile] });
    } else {
      Object.keys(errorLabels)
        .filter((errorType) => parsedFile.errorType[errorType])
        .forEach((errorType) => {
          errors.push({ title: errorLabels[errorType], items: parsedFile.errorType[errorType] });
        });
      if (parsedFile.hasBlankRows) {
        errors.push({ title: locale.otherError, items: [locale.blankRowsOrColumnsInFile] });
      }
    }
    if (errors.length) {
      importModal.close();
      resultModal.show({
        title: locale.importFailed,
        isSuccess: false,
        content: (
          <div style={{ width: "420px" }}>
            {errors.map((error, index) => (
              <div key={index} className="flex" style={{ marginTop: "20px" }}>
                <div style={{ width: "250px" }}>
                  <Text subtitle>{error.title}</Text>
                </div>
                <div>
                  <Text strong>
                    {error.items.slice(0, 5).join(", ")}
                    {error.items.length > 5 && `, and ${error.items.length - 5} more others.`}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        ),
      });
      return;
    }

    const fuelCodesRequest = parsedFile.items.map((row) => {
      const driver = isAny(row.driverLicenseId)
        ? null
        : driversByLicenseId.get(row.driverLicenseId.trim().toUpperCase());
      const vehicle = isAny(row.plateNumber)
        ? null
        : vehiclesByPlateNumber.get(row.plateNumber.trim().toUpperCase());
      const startDate = moment(row.startDate.trim(), "YYYY-MM-DD");
      const sendFuelCode = isYes(row.sendAsSms);
      const mobileNumber = row.mobileNumber?.trim() || "";

      let fuelCodeType = FuelCodeType.AnyDriverAnyVehicle;
      if (driver && vehicle) {
        fuelCodeType = FuelCodeType.DriverVehicleSpecific;
      } else if (driver) {
        fuelCodeType = FuelCodeType.DriverSpecific;
      } else if (vehicle) {
        fuelCodeType = FuelCodeType.VehicleSpecific;
      }

      return {
        fleetId,
        fuelCodeType,
        productCode: row.productCode.trim().toLowerCase(),
        literVolume: Number(row.literVolume.trim()),
        startDate: formatDate(startDate, DateTime.J),
        endDate: formatDate(
          startDate.clone().add(Number(row.durationDays.trim()) - 1, "days"),
          DateTime.K,
        ),
        maxUsage: isYes(row.oneTimeUse) ? 1 : 0,
        autoRenew: isYes(row.autoRenew),
        sendFuelCode,
        ...(driver ? { driverIds: [driver.driverId] } : {}),
        ...(vehicle ? { vehicleIds: [vehicle.vehicleId] } : {}),
        ...(!driver && sendFuelCode
          ? { mobileNumber: mobileNumber.length === 10 ? `0${mobileNumber}` : mobileNumber }
          : {}),
      };
    });

    await generateRequest.request({ body: fuelCodesRequest });
    importModal.close();
    resultModal.show({
      title: locale.importSuccessful,
      isSuccess: true,
      content: (
        <div style={{ width: "400px", textAlign: "center" }}>
          <locale.Populate
            text={locale.youHaveSuccessfullyUploadedFuelCodes}
            items={[
              <strong key="count">
                {locale.populate(locale.nFuelCodes, [fuelCodesRequest.length])}
              </strong>,
            ]}
          />
        </div>
      ),
    });
  };

  return (
    <div>
      <Modal {...resultModal}>
        {resultModal.content}
        <ActionButton
          right={!resultModal.isSuccess}
          center={resultModal.isSuccess}
          items={
            resultModal.isSuccess
              ? [
                  {
                    text: locale.gotIt,
                    onClick: () => {
                      resultModal.close();
                    },
                    primary: true,
                  },
                ]
              : [
                  {
                    text: locale.gotIt,
                    onClick: () => {
                      resultModal.close();
                    },
                  },
                  {
                    text: locale.retry,
                    onClick: () => {
                      resultModal.close();
                      importModal.show();
                    },
                  },
                ]
          }
        />
      </Modal>
      <ImportModal
        {...importModal}
        subtitle={locale.followTheFollowingColumnOrderAndLeaveRow1}
        loading={getDriversRequest.loading || getVehiclesRequest.loading || generateRequest.loading}
        onSubmit={importFuelCodes}
      >
        <div className={styles.instructions}>
          {[
            locale.fuelCodeImportColumnProductCode,
            locale.fuelCodeImportColumnVolumeRedeemLimit,
            locale.fuelCodeImportColumnStartDate,
            locale.fuelCodeImportColumnDuration,
            locale.fuelCodeImportColumnDriverLicenseId,
            locale.fuelCodeImportColumnPlateNumber,
            locale.fuelCodeImportColumnAutoRenew,
            locale.fuelCodeImportColumnOneTimeUse,
            locale.fuelCodeImportColumnSendAsSms,
            locale.fuelCodeImportColumnContactNumber,
          ].map((column, index) => (
            <div key={index}>{locale.populate(locale.columnNTitle, [index + 1, column])}</div>
          ))}
          <div>
            {locale.downloadASampleTemplate.split("{0}")[0]}
            <span
              className={styles.sampleTemplate}
              onClick={() => {
                exportCSV({
                  rows: [
                    "Product Code,Volume Redeem Limit,Eligible Period Start Date,Eligible Period Duration in Days,Driver License ID,Plate Number,Auto Renew,One-time Use,Send as SMS,Driver Contact Number",
                    "diesel,100,2026-01-01,30,Any,ABC1234,0,1,1,09123456789",
                  ],
                  filename: "Fuel Code Template",
                });
              }}
            >
              {locale.sampleTemplate}
            </span>
            {locale.downloadASampleTemplate.split("{0}")[1]}
          </div>
        </div>
      </ImportModal>
    </div>
  );
};

export default ImportFuelCodeModal;
